/**
 * CustomerStatFieldInfo - Static applicant fields grid for ticket creation
 * Adapted from Thaklis app NewTicket/panels/CustomerStatFieldInfo.js to use ThaklisApiService.
 *
 * Document fields write their value directly into Formik (deferBulkUpload + allowMultiple); the
 * submit-time write-back reads them back from Formik values. Multiple-instance certificates
 * (educational / experience) use TicketMultiCertificateField.
 */
import React from 'react';
import { Box, Grid, TextField, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Field } from 'formik';
import FileField from './FileField';
import TicketMultiCertificateField from './TicketMultiCertificateField';
import { useFieldStructure } from '../FieldStructureContext';

dayjs.extend(utc);

const CustomerStatFieldInfo = (props) => {
  const fieldStructureContext = useFieldStructure();

  const {
    staticFields,
    isUidready,
    setFieldValue,
    formikValues,
    employeeUid,
  } = props;

  const renderTextField = (fieldName, label) => {
    if (!staticFields.includes(fieldName)) return null;
    return (
      <Grid item xs={6} my={1} key={fieldName}>
        <Field name={fieldName} label={label} fullWidth as={TextField} disabled={!isUidready} />
      </Grid>
    );
  };

  const renderDateField = (fieldName, label) => {
    if (!staticFields.includes(fieldName)) return null;
    return (
      <Grid item xs={6} my={1} key={fieldName}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Field
            name={fieldName}
            as={DatePicker}
            disabled={!isUidready}
            inputFormat="DD-MM-YYYY"
            label={label}
            onChange={(newValue) => {
              let formattedDate = dayjs.utc(newValue).local().format('YYYY-MM-DD');
              setFieldValue(fieldName, dayjs.utc(formattedDate, 'YYYY-MM-DD'));
            }}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
        </LocalizationProvider>
      </Grid>
    );
  };

  // Single-instance document field: value lives directly in Formik. deferBulkUpload leaves the
  // employee-profile write to submit-time syncNewUploadsToEmployeeDocuments; allowMultiple lets a
  // single field hold several files saved as one group.
  const renderDocField = (fieldName, title, type = 'PDF') => {
    if (!staticFields.includes(fieldName)) return null;
    return (
      <Grid item xs={6} my={1} key={fieldName}>
        <FileField
          field={{ name: fieldName, value: formikValues?.[fieldName], title, type }}
          onFileUpload={(response) => setFieldValue(fieldName, response)}
          onFileRemove={() => setFieldValue(fieldName, null)}
          employeeUid={employeeUid}
          contextProps={fieldStructureContext}
          deferBulkUpload
          allowMultiple
        />
      </Grid>
    );
  };

  // Multiple-instance certificate field (several titled certificates, each with multiple files).
  const renderMultiCert = (fieldName, title) => {
    if (!staticFields.includes(fieldName)) return null;
    return (
      <Grid item xs={6} my={1} key={fieldName}>
        <TicketMultiCertificateField
          field={{ name: fieldName, title }}
          value={formikValues?.[fieldName]}
          onChange={(v) => setFieldValue(fieldName, v)}
          employeeUid={employeeUid}
          disabled={!isUidready}
        />
      </Grid>
    );
  };

  return (
    <Box my={2}>
      {staticFields.length > 0 && !isUidready ? (
        <Typography fontSize={10} color="red">
          ** Search for uid details to make below fields active
        </Typography>
      ) : null}
      <Grid container spacing={1}>
        {renderTextField('firstNameEn', 'First Name (English)')}
        {renderTextField('middleNameEn', 'Middle Name (English)')}
        {renderTextField('lastNameEn', 'Last Name (English)')}
        {renderTextField('firstNameAr', 'First Name (Arabic)')}
        {renderTextField('middleNameAr', 'Middle Name (Arabic)')}
        {renderTextField('lastNameAr', 'Last Name (Arabic)')}
        {renderTextField('nameEn', 'Name (English)')}
        {renderTextField('nameAr', 'Name (Arabic)')}
        {renderDateField('dob', 'Date Of Birth')}

        {staticFields.includes('gender') && (
          <Grid item xs={6} my={1}>
            <FormControl fullWidth disabled={!isUidready}>
              <InputLabel>Gender</InputLabel>
              <Field name="gender" as={Select} label="Gender" disabled={!isUidready}>
                <MenuItem value="MALE">Male</MenuItem>
                <MenuItem value="FEMALE">Female</MenuItem>
              </Field>
            </FormControl>
          </Grid>
        )}

        {renderTextField('nationality', 'Nationality')}
        {renderTextField('passportNumber', 'Passport Number')}
        {renderDateField('passportIssue', 'Passport Issue')}
        {renderDateField('passportExpiry', 'Passport Expiry')}
        {renderTextField('passportIssuePlaceEn', 'Passport Issue Place (English)')}
        {renderTextField('passportIssuePlaceAr', 'Passport Issue Place (Arabic)')}
        {renderTextField('emiratesId', 'Emirates Id')}
        {renderTextField('emirates_id_number', 'Emirates ID Number')}
        {renderDateField('emirates_id_expiry_date', 'Emirates ID Expiry')}
        {renderDateField('emiratesIdIssueDate', 'Emirates Id Issue Date')}
        {renderDateField('emiratesIdExpiryDate', 'Emirates Id Expiry Date')}
        {renderTextField('birthPlaceEn', 'Birth Place (English)')}
        {renderTextField('birthPlaceAr', 'Birth Place (Arabic)')}
        {renderTextField('emirate', 'Emirate')}
        {renderTextField('address', 'Address')}
        {renderTextField('area', 'Area')}
        {renderTextField('buildingNumber', 'Building Number')}
        {renderTextField('poBox', 'PO Box')}
        {renderTextField('mobileNumber', 'Mobile Number')}
        {renderTextField('landNumber', 'Land Number')}
        {renderTextField('deliveryNumber', 'Delivery Number')}
        {renderTextField('email', 'Email')}
        {renderTextField('fileNumber', 'File Number')}
        {renderDateField('visaIssueDate', 'Visa Issue Date')}
        {renderDateField('visaExpiryDate', 'Visa Expiry Date')}

        {/* Document fields (single-instance, multi-file capable) */}
        {renderDocField('birthCertificate', 'Birth Certificate', 'PDF')}
        {renderDocField('passportCopy', 'Passport Copy', 'PDF')}
        {renderDocField('visaPage', 'Visa Page', 'PDF')}
        {renderDocField('photo', 'Photo', 'IMAGE')}
        {renderDocField('shinasnama', 'Shinasnama', 'PDF')}
        {renderDocField('labourContract', 'Labour Contract', 'PDF')}
        {renderDocField('salaryCertificate', 'Salary Certificate', 'PDF')}
        {renderDocField('resume', 'Resume / CV', 'DOCUMENT')}
        {renderDocField('policeClearanceCertificate', 'Police Clearance Certificate', 'PDF')}

        {/* Multiple-instance certificates */}
        {renderMultiCert('educationalCertificate', 'Educational Certificate')}
        {renderMultiCert('experienceCertificate', 'Experience Certificate')}
      </Grid>
    </Box>
  );
};

export default CustomerStatFieldInfo;
